import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation, Link } from 'react-router-dom';
import { Navigation } from './components/Navigation';
import { ChatWidget } from './components/ChatWidget';
import { CommandPalette } from './components/CommandPalette';
import { ContactModal } from './components/ContactModal';
import { RequestDeckModal } from './components/RequestDeckModal';
import { MeshBackground } from './components/LiquidComponents';
import { Home } from './pages/Home';
import { Portfolio } from './pages/Portfolio';
import { ProjectDetails } from './pages/ProjectDetails';
import { About } from './pages/About';
import { Privacy } from './pages/Privacy';
import { Admin } from './pages/Admin';
import { NotFound } from './pages/NotFound';
import TelegramApp from './pages/TelegramApp';
import { Github, Linkedin } from 'lucide-react';
import { TelegramIcon } from './components/icons/TelegramIcon';
import { TiwazIcon } from './components/icons/TiwazIcon';
import { useData } from './contexts/DataContext';
import { founderLinks } from './constants/founderLinks';
import { resolveProjectSlug } from './utils/projectSlug';
import { Project } from './types';
import Lenis from 'lenis';
import { setLenis, scrollToTop } from './services/smoothScroll';

// KILL SWITCH — flip to false to instantly disable smooth scroll everywhere.
// With it off (or on any non-qualifying device), no Lenis instance is ever created,
// getLenis() returns null, and every rewired scroll call falls back to native
// behavior, so the site behaves exactly as it did before this feature.
const ENABLE_SMOOTH_SCROLL = true;

// Custom X (Twitter) Logo Component
const XLogo = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className={className} fill="currentColor" width="18" height="18">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

// Scroll handling component. Route changes reset to the top; routed through the
// smoothScroll helper so that when Lenis is active the reset uses lenis.scrollTo
// (immediate) and stays in sync with Lenis's internal target instead of being
// snapped back on the next frame. Falls back to native window.scrollTo otherwise.
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    scrollToTop(true);
  }, [pathname]);

  return null;
};

// Desktop-only smooth scroll (Lenis). Premium and subtle by design: a short 0.9s
// glide on an expo-out curve (fast pickup, gentle settle) rather than the heavier
// ~1.2s default, so it reads as responsive, not floaty.
//
// Gating: initialized ONLY on precise-pointer, wide-viewport devices that have not
// asked for reduced motion, and never on /admin or /tg-app. Touch phones/tablets
// (pointer: coarse) are excluded entirely, so native momentum scrolling there is
// left completely untouched. The effect keys on whether the current route is
// excluded, so a single instance persists across normal navigations and is only
// torn down / rebuilt when crossing the /admin or /tg-app boundary.
const SmoothScroll = () => {
  const { pathname } = useLocation();
  const isExcludedRoute =
    pathname.startsWith('/admin') || pathname.startsWith('/tg-app');

  useEffect(() => {
    if (!ENABLE_SMOOTH_SCROLL) return;
    if (isExcludedRoute) return;
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;

    const qualifies =
      window.matchMedia('(min-width: 768px)').matches &&
      window.matchMedia('(pointer: fine)').matches &&
      !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!qualifies) return;

    const lenis = new Lenis({
      duration: 0.9,
      easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)), // expo-out
      smoothWheel: true,
      wheelMultiplier: 1,
      syncTouch: false,
    });
    setLenis(lenis);

    let rafId = requestAnimationFrame(function raf(time: number) {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    });

    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      setLenis(null);
    };
  }, [isExcludedRoute]);

  return null;
};

const CANONICAL_DOMAIN = 'https://www.helenendorfsky.tech';

const SEO_AUTHOR = 'Ruhlan Jafarli';

const DEFAULT_SEO = {
  title: 'Ruhlan Jafarli | HELENENDORFSKY · Serial Founder',
  description:
    'Ruhlan Jafarli, also known as helenendorfsky, is a tech entrepreneur who builds and scales ventures across blockchain, AI, fintech, digital identity, and beyond.',
};

// Per-route meta. Keys are normalized pathnames (no trailing slash; root is '/').
const ROUTE_SEO: Record<string, { title: string; description: string }> = {
  '/': DEFAULT_SEO,
  '/portfolio': {
    title: `Venture Portfolio · ${SEO_AUTHOR} | HELENENDORFSKY`,
    description:
      'The venture portfolio of Ruhlan Jafarli (helenendorfsky): Sevenoir, Wealthernal, Oraculore, Sycloop, and more across AI, blockchain, and digital identity.',
  },
  '/about': {
    title: `About · ${SEO_AUTHOR} | HELENENDORFSKY`,
    description:
      'About Ruhlan Jafarli (helenendorfsky): Munich-based serial solo founder building ventures across AI, blockchain, fintech, and digital identity.',
  },
  '/tg-app': {
    title: `App | ${SEO_AUTHOR} (helenendorfsky)`,
    description:
      'The Ruhlan Jafarli (helenendorfsky) mini app: explore his ventures, portfolio, and background in one place.',
  },
  '/privacy': {
    title: `Privacy Policy | ${SEO_AUTHOR} (helenendorfsky)`,
    description: 'How helenendorfsky.tech collects, uses, and protects your data.',
  },
  '/admin': DEFAULT_SEO,
};

// Meta for the catch-all NotFound route and dead project slugs. noindex keeps
// soft-404s (the SPA rewrite always answers 200) out of the index.
const NOT_FOUND_SEO = {
  title: `Page Not Found | ${SEO_AUTHOR}`,
  description: DEFAULT_SEO.description,
  noindex: true,
};

const buildCanonicalUrl = (pathname: string) => {
  if (!pathname || pathname === '/') {
    return `${CANONICAL_DOMAIN}/`;
  }

  const normalizedPath = pathname.replace(/\/+$/, '');
  return `${CANONICAL_DOMAIN}${normalizedPath.startsWith('/') ? normalizedPath : `/${normalizedPath}`}`;
};

const ensureHttp = (url: string) => {
  const trimmed = (url || '').trim();
  if (!trimmed) return '';
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

// Keep meta descriptions in a sane length range for search/social previews.
const clampDescription = (text: string, max = 160) => {
  const clean = text.replace(/\s+/g, ' ').trim();
  if (clean.length <= max) return clean;
  const sliced = clean.slice(0, max - 1);
  const lastSpace = sliced.lastIndexOf(' ');
  return `${(lastSpace > 40 ? sliced.slice(0, lastSpace) : sliced).trimEnd()}…`;
};

// Routes that must never be indexed: the admin panel and the Telegram wrapper.
// The raw HTML is shared across routes, so per-route noindex has to be applied
// at runtime here rather than in the static entries.
const NOINDEX_ROUTES = ['/admin', '/tg-app'];

// Add or remove a robots noindex directive for the current route.
const setRobotsNoindex = (active: boolean) => {
  let el = document.head.querySelector<HTMLMetaElement>('meta[name="robots"]');
  if (active) {
    if (!el) {
      el = document.createElement('meta');
      el.setAttribute('name', 'robots');
      document.head.appendChild(el);
    }
    el.setAttribute('content', 'noindex');
  } else if (el) {
    el.remove();
  }
};

// Create the meta tag if it does not exist yet, then set its content.
const upsertMeta = (key: 'name' | 'property', value: string, content: string) => {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${key}="${value}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(key, value);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

// Resolve the title/description for the current route. For project detail routes
// (/portfolio/:slug) the values come from the specific Supabase project. If the
// project list has not resolved yet (or the slug is unknown) we fall back to the
// generic portfolio meta so we never render title/description from empty data.
const resolveRouteSeo = (
  pathname: string,
  projects: Project[],
): { title: string; description: string; noindex?: boolean } => {
  const normalized = pathname.replace(/\/+$/, '') || '/';

  const projectMatch = normalized.match(/^\/portfolio\/(.+)$/);
  if (projectMatch) {
    const slug = decodeURIComponent(projectMatch[1]);
    const project = projects.find((p) => resolveProjectSlug(p) === slug || p.id === slug);
    if (project) {
      const name = (project.title || project.name || 'Project').trim();
      const description = (project.description || '').trim();
      return {
        title: `${name} | ${SEO_AUTHOR}`,
        description: clampDescription(description || DEFAULT_SEO.description),
      };
    }
    // Projects loaded and the slug matched nothing: a dead URL, keep it out
    // of the index. While projects are still loading, stay neutral so real
    // project pages are never briefly marked noindex.
    if (projects.length > 0) {
      return NOT_FOUND_SEO;
    }
    return ROUTE_SEO['/portfolio'];
  }

  if (ROUTE_SEO[normalized]) return ROUTE_SEO[normalized];

  // Unknown pathname: the catch-all NotFound route is rendering.
  return NOT_FOUND_SEO;
};

// Rebuild the Person schema's "founder" list from the live Supabase projects so the
// structured data always matches the real portfolio. All other Person fields in the
// existing JSON-LD block are preserved untouched.
const updateFounderJsonLd = (projects: Project[]) => {
  // Target the Person block by id: the head now carries a second JSON-LD
  // script (WebSite), so the bare type selector would be ambiguous.
  const script = document.head.querySelector<HTMLScriptElement>(
    'script#person-jsonld[type="application/ld+json"]',
  );
  if (!script || !script.textContent) return;

  let data: Record<string, unknown>;
  try {
    data = JSON.parse(script.textContent);
  } catch {
    return; // Leave the static JSON-LD untouched if it can't be parsed.
  }

  const founder: Array<Record<string, string>> = [];
  const seen = new Set<string>();
  for (const project of projects) {
    const name = (project.title || project.name || '').trim();
    if (!name) continue;
    const key = name.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);

    const entry: Record<string, string> = { '@type': 'Organization', name };
    const url = ensureHttp(project.websiteUrl || '');
    if (url) entry.url = url;
    founder.push(entry);
  }

  // Don't wipe the existing founder list before the projects have loaded.
  if (founder.length === 0) return;

  data.founder = founder;
  script.textContent = JSON.stringify(data, null, 2);
};

const SeoManager = () => {
  const { pathname } = useLocation();
  const { projects } = useData();

  // Canonical + og:url + per-route / per-project title and description.
  useEffect(() => {
    if (typeof document === 'undefined') return;

    const canonicalUrl = buildCanonicalUrl(pathname);

    let canonicalLink = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', canonicalUrl);

    let ogUrlMeta = document.querySelector<HTMLMetaElement>('meta[property="og:url"]');
    if (!ogUrlMeta) {
      ogUrlMeta = document.createElement('meta');
      ogUrlMeta.setAttribute('property', 'og:url');
      document.head.appendChild(ogUrlMeta);
    }
    ogUrlMeta.setAttribute('content', canonicalUrl);

    const { title, description, noindex } = resolveRouteSeo(pathname, projects);
    document.title = title;
    upsertMeta('name', 'description', description);
    upsertMeta('property', 'og:title', title);
    upsertMeta('property', 'og:description', description);
    upsertMeta('name', 'twitter:title', title);
    upsertMeta('name', 'twitter:description', description);

    const normalizedPath = pathname.replace(/\/+$/, '') || '/';
    const noindexRoute = NOINDEX_ROUTES.some(
      (route) => normalizedPath === route || normalizedPath.startsWith(`${route}/`),
    );
    setRobotsNoindex(Boolean(noindex) || noindexRoute);
  }, [pathname, projects]);

  // Keep the Person JSON-LD founder list in sync with the live projects.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    updateFounderJsonLd(projects);
  }, [projects]);

  return null;
};

// Footer теперь правильно обрабатывает отсутствие данных
const Footer = () => {
  const { profile } = useData();

  const openContact = (e: React.MouseEvent) => {
    e.preventDefault();
    window.dispatchEvent(new Event('open-contact-modal'));
  };

  // Footer nav mirrors the site's primary routes. Kept local (not imported from
  // Navigation) so the footer's link set stays intentional and stable.
  const exploreLinks = [
    { to: '/', label: 'Home' },
    { to: '/portfolio', label: 'Portfolio' },
    { to: '/about', label: 'About' },
    { to: '/privacy', label: 'Privacy Policy' },
  ];

  const hasSocials =
    !!profile?.socials?.linkedin ||
    !!profile?.socials?.twitter ||
    !!profile?.socials?.github ||
    !!profile?.socials?.telegram;

  const socialClass =
    'text-zinc-600 dark:text-zinc-300 hover:text-zinc-900 dark:hover:text-white transition-colors duration-300';
  const headingClass =
    'text-[11px] font-semibold uppercase tracking-[0.2em] text-zinc-500 dark:text-zinc-400';
  const linkClass =
    'text-sm font-light text-zinc-600 dark:text-zinc-300 hover:text-zinc-900 dark:hover:text-white transition-colors';

  return (
    <footer className="relative mt-auto overflow-hidden border-t border-black/5 dark:border-white/5 bg-zinc-50/50 dark:bg-black/20 [.nardogrey_&]:bg-black/10 pt-20 pb-10 backdrop-blur-sm transition-colors duration-500">
      {/* Top gradient hairline — a quiet premium accent across the seam */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-zinc-400/40 dark:via-white/15 to-transparent" />

      <div className="relative container mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 gap-x-8 gap-y-12 md:grid-cols-12">

          {/* Brand block — star anchor + wordmark, signature, description, socials */}
          <div className="md:col-span-12 lg:col-span-4">
            <Link
              to="/"
              aria-label="HELENENDORFSKY home"
              className="inline-flex items-center gap-4"
            >
              {/* Decorative: the wordmark right next to it carries the name,
                  so a text alt would just duplicate it for extractors/AT. */}
              <img
                src="/favicon-512x512.png"
                alt=""
                width={56}
                height={56}
                className="h-14 w-14 select-none"
              />
              <span className="text-base sm:text-lg font-bold uppercase leading-none tracking-[0.2em] text-zinc-900 dark:text-white">
                HELENENDORFSKY
              </span>
            </Link>

            <p className="mt-5 text-[11px] font-light lowercase tracking-[0.2em] text-zinc-500 dark:text-zinc-400">
              success is inevitable.
            </p>

            <p className="mt-4 max-w-xs text-sm font-light leading-relaxed text-zinc-600 dark:text-zinc-300">
              Serial solo founder building technology ventures across AI, fintech,
              blockchain, IoT, and digital infrastructure.
            </p>

            {hasSocials && (
              <div className="mt-7 flex items-center gap-5">
                {profile?.socials?.linkedin && (
                  <a href={profile.socials.linkedin} target="_blank" rel="noreferrer" aria-label="LinkedIn" className={socialClass}>
                    <Linkedin size={23} strokeWidth={1.75} />
                  </a>
                )}
                {profile?.socials?.twitter && (
                  <a href={profile.socials.twitter} target="_blank" rel="noreferrer" aria-label="X" className={socialClass}>
                    <XLogo className="h-[21px] w-[21px]" />
                  </a>
                )}
                {profile?.socials?.github && (
                  <a href={profile.socials.github} target="_blank" rel="noreferrer" aria-label="GitHub" className={socialClass}>
                    <Github size={23} strokeWidth={1.75} />
                  </a>
                )}
                {profile?.socials?.telegram && (
                  <a href={profile.socials.telegram} target="_blank" rel="noreferrer" aria-label="Telegram" className={socialClass}>
                    <TelegramIcon size={22} />
                  </a>
                )}
              </div>
            )}
          </div>

          {/* Explore */}
          <nav className="md:col-span-4 lg:col-span-2" aria-label="Explore">
            <h5 className={headingClass}>Explore</h5>
            <ul className="mt-5 space-y-3">
              {exploreLinks.map((item) => (
                <li key={item.to}>
                  <Link to={item.to} className={linkClass}>
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* Profiles & Writing — brand-icon links, now a clean vertical list */}
          <nav className="md:col-span-4 lg:col-span-3" aria-label="Profiles and writing">
            <h5 className={headingClass}>Profiles &amp; Writing</h5>
            <ul className="mt-5 space-y-3">
              {founderLinks.map((link) => (
                <li key={link.id}>
                  <a
                    href={link.href}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={link.label}
                    className="inline-flex items-center gap-3 text-sm font-light text-zinc-600 dark:text-zinc-300 hover:text-zinc-900 dark:hover:text-white transition-colors"
                  >
                    <link.icon size={20} className="shrink-0" />
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          {/* Contact */}
          <div className="md:col-span-4 lg:col-span-3">
            <h5 className={headingClass}>Contact</h5>
            <p className="mt-5 max-w-xs text-sm font-light leading-relaxed text-zinc-600 dark:text-zinc-300">
              Open to strategic partnerships, investment conversations, media
              inquiries, and ideas worth discussing.
            </p>
            <button
              onClick={openContact}
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-zinc-900 px-6 py-2.5 text-sm font-medium text-white shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg active:translate-y-0 dark:bg-white dark:text-zinc-900"
            >
              Start a Conversation
            </button>

            <div className="mt-7">
              <h6 className={headingClass}>Press &amp; Investors</h6>
              <a
                href="mailto:ruhlan@sevenoir.com"
                className="mt-2 inline-block text-sm font-light text-zinc-600 dark:text-zinc-300 hover:text-zinc-900 dark:hover:text-white transition-colors"
              >
                ruhlan@sevenoir.com
              </a>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-16 flex flex-col items-center justify-between gap-4 border-t border-black/5 dark:border-white/5 pt-8 sm:flex-row">
          <p className="text-xs font-light text-zinc-400 dark:text-zinc-600 [.nardogrey_&]:text-zinc-400">
            Ruhlan Jafarli (helenendorfsky) © 2026. All rights reserved.
          </p>

          <button
            onClick={() => scrollToTop(false)}
            aria-label="Back to top"
            className="group inline-flex items-center gap-2.5 text-xs text-zinc-500 transition-colors duration-300 hover:text-zinc-900 dark:text-zinc-500 dark:hover:text-white"
          >
            Back to Top
            <TiwazIcon size={20} className="shrink-0" />
          </button>
        </div>
      </div>
    </footer>
  );
};

const AppContent = () => {
  const location = useLocation();
  
  return (
    <div className="min-h-screen bg-zinc-100 dark:bg-zinc-950 [.nardogrey_&]:bg-[#686A6C] text-zinc-900 dark:text-zinc-100 font-sans selection:bg-blue-500/30 selection:text-blue-900 dark:selection:text-blue-200 transition-colors duration-700">
       <div className="fixed inset-0 z-0 pointer-events-none transition-opacity duration-700">
         {/* Light Mode Gradient Base */}
         <div className="absolute inset-0 bg-gradient-to-br from-zinc-50 via-zinc-100 to-zinc-200 opacity-100 dark:opacity-0" />
         
         {/* Dark Mode Gradient Base */}
         <div className="hidden md:block absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-zinc-900 via-zinc-950 to-black opacity-0 dark:opacity-80 [.nardogrey_&]:opacity-0" />

         {/* Nardo Grey Background - Specific Flat Grey #686A6C */}
         <div className="absolute inset-0 bg-[#686A6C] opacity-0 [.nardogrey_&]:opacity-100 transition-opacity duration-700" />
         
         {/* Living Background Mesh - MOVED TO END TO RENDER ON TOP */}
         <MeshBackground />
       </div>
       
       <div className="relative z-10 flex flex-col min-h-screen">
        <SeoManager />
        <SmoothScroll />
        <ScrollToTop />
        <Navigation />
        <CommandPalette />
        <ContactModal />
        <RequestDeckModal />
         <Routes>
           <Route path="/" element={<Home />} />
           <Route path="/portfolio" element={<Portfolio />} />
            <Route path="/portfolio/:slug" element={<ProjectDetails />} />
           <Route path="/about" element={<About />} />
           <Route path="/privacy" element={<Privacy />} />
           <Route path="/admin" element={<Admin />} />
           <Route path="/tg-app" element={<TelegramApp />} />
           <Route path="*" element={<NotFound />} />
         </Routes>
         <Footer />
        </div>

       {/* Chat Widget only visible on non-admin pages */}
       {!location.pathname.startsWith('/admin') && <ChatWidget />}
    </div>
  );
};

const App: React.FC = () => {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
};

export default App;
